import { ArrowDropDown } from '@mui/icons-material';
import { Box, Button, Checkbox, TextField } from '@mui/material';
import { useMemo, useState } from 'react';
import { FixedSizeList } from 'react-window';

// ─── Shared list sizing ───────────────────────────────────────────────────────

const VIRTUAL_ROW_HEIGHT  = 34;
const VIRTUAL_MAX_VISIBLE = 8;

// ─── Virtualized Value Dropdown ───────────────────────────────────────────────
// When a filterValues catalog has many items, we render a virtualized list with
// a search input for fast lookup instead of dumping all MenuItems at once.

export const VirtualizedValueDropdown = ({ valueCatalog, selectedValue, onChange, error }) => {
    const [searchTerm, setSearchTerm] = useState("");
    const [anchorEl, setAnchorEl]     = useState(null);
    const open = Boolean(anchorEl);

    const filteredItems = useMemo(
        () => valueCatalog.filter((item) =>
            String(item.label || "").toLowerCase().includes(searchTerm.toLowerCase())
        ),
        [valueCatalog, searchTerm]
    );

    const selectedLabel = useMemo(() => {
        if (selectedValue === "" || selectedValue === undefined || selectedValue === null) return null;
        return valueCatalog.find(v => String(v.value) === String(selectedValue))?.label ?? selectedValue;
    }, [valueCatalog, selectedValue]);

    const handleOpen = (e) => setAnchorEl(e.currentTarget);
    const handleClose = () => { setAnchorEl(null); setSearchTerm(""); };

    const Row = ({ index: rowIndex, style }) => {
        const item = filteredItems[rowIndex];
        const isSelected = String(item.value) === String(selectedValue);
        return (
            <Box
                key={item.value}
                style={style}
                sx={{
                    display: "flex",
                    alignItems: "center",
                    px: "12px",
                    cursor: "pointer",
                    backgroundColor: isSelected ? "#EBF9F4" : "transparent",
                    "&:hover": { backgroundColor: "#f5f5f5" },
                }}
                onClick={() => { onChange(item.value); handleClose(); }}
            >
                <span style={{ fontSize: "12px", color: "#373F41" }}>{item.label}</span>
            </Box>
        );
    };

    return (
        <>
            {/* Trigger — looks like a normal Select */}
            <Box
                onClick={handleOpen}
                sx={{
                    height: "28px",
                    fontSize: "12px",
                    fontFamily: "Inter, sans-serif",
                    fontWeight: 400,
                    backgroundColor: "white",
                    border: error ? "1px solid #d32f2f" : "1px solid #C4C4C4",
                    borderRadius: "4px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    px: "10px",
                    cursor: "pointer",
                    "&:hover": { borderColor: error ? "#d32f2f" : "#61D5A9" },
                }}
            >
                {selectedLabel
                    ? <span style={{ fontSize: "12px", color: "#373F41" }}>{selectedLabel}</span>
                    : <span style={{ fontSize: "12px", color: error ? "#d32f2f" : "#bab7b7" }}>Select Value</span>
                }
                <ArrowDropDown sx={{ fontSize: "20px", color: "#BAB7B7", mr: "-6px" }} />
            </Box>

            {/* Popover-style dropdown */}
            {open && (
                <Box
                    sx={{
                        position: "absolute",
                        zIndex: 1300,
                        mt: "2px",
                        bgcolor: "white",
                        border: "1px solid #C4C4C4",
                        borderRadius: "8px",
                        boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
                        width: "100%",
                        minWidth: "200px",
                    }}
                >
                    {/* Search input */}
                    <Box sx={{ p: "8px 12px", borderBottom: "1px solid #eee" }}>
                        <TextField
                            size="small"
                            variant="standard"
                            placeholder="Search…"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            autoFocus
                            fullWidth
                            sx={{ fontSize: "12px", "& input": { fontSize: "12px", fontFamily: "Inter" } }}
                        />
                    </Box>

                    {/* Virtualized list */}
                    {filteredItems.length > 0 ? (
                        <FixedSizeList
                            height={Math.min(filteredItems.length, VIRTUAL_MAX_VISIBLE) * VIRTUAL_ROW_HEIGHT}
                            itemCount={filteredItems.length}
                            itemSize={VIRTUAL_ROW_HEIGHT}
                            width="100%"
                        >
                            {Row}
                        </FixedSizeList>
                    ) : (
                        <Box sx={{ p: "12px", textAlign: "center" }}>
                            <span style={{ fontSize: "12px", color: "#bab7b7" }}>No results</span>
                        </Box>
                    )}
                </Box>
            )}

            {/* Click-away listener */}
            {open && (
                <Box
                    onClick={handleClose}
                    sx={{ position: "fixed", inset: 0, zIndex: 1299 }}
                />
            )}
        </>
    );
};

// ─── Multi-select Value Dropdown (eq / neq → IN / NOT IN) ─────────────────────
// Lets the user pick several catalog values at once. Search + virtualized list
// with checkboxes, selected values surfaced as chips in the trigger.

export const MultiSelectValueDropdown = ({ valueCatalog, selectedValues = [], onChange, error }) => {
    const [searchTerm, setSearchTerm] = useState("");
    const [anchorEl, setAnchorEl]     = useState(null);
    const open = Boolean(anchorEl);

    // Normalize to an array of strings for comparison.
    const selectedSet = useMemo(
        () => new Set((selectedValues || []).map(v => String(v))),
        [selectedValues]
    );

    const filteredItems = useMemo(
        () => valueCatalog.filter((item) =>
            String(item.label || "").toLowerCase().includes(searchTerm.toLowerCase())
        ),
        [valueCatalog, searchTerm]
    );

    const selectedChips = useMemo(
        () => valueCatalog.filter(v => selectedSet.has(String(v.value))),
        [valueCatalog, selectedSet]
    );

    // Compact trigger summary: "Facebook, Instagram and 2 others selected".
    const summaryText = useMemo(() => {
        const labels = selectedChips.map(c => c.label);
        if (labels.length === 0) return null;
        if (labels.length <= 2) return labels.join(", ");
        return `${labels.slice(0, 2).join(", ")} and ${labels.length - 2} others selected`;
    }, [selectedChips]);

    const handleOpen  = (e) => setAnchorEl(e.currentTarget);
    const handleClose = () => { setAnchorEl(null); setSearchTerm(""); };

    const toggleValue = (val) => {
        const key = String(val);
        const next = selectedSet.has(key)
            ? (selectedValues || []).filter(v => String(v) !== key)
            : [...(selectedValues || []), val];
        onChange(next);
    };

    // Select all currently-filtered values (keeps any prior selections),
    // and clear all removes every selection.
    const selectAllFiltered = () => {
        const merged = [...(selectedValues || [])];
        filteredItems.forEach(i => { if (!selectedSet.has(String(i.value))) merged.push(i.value); });
        onChange(merged);
    };
    const clearAll = () => onChange([]);

    const Row = ({ index: rowIndex, style }) => {
        const item = filteredItems[rowIndex];
        const isSelected = selectedSet.has(String(item.value));
        return (
            <Box
                key={item.value}
                style={style}
                sx={{
                    display: "flex",
                    alignItems: "center",
                    px: "6px",
                    cursor: "pointer",
                    backgroundColor: isSelected ? "#EBF9F4" : "transparent",
                    "&:hover": { backgroundColor: "#f5f5f5" },
                }}
                onClick={() => toggleValue(item.value)}
            >
                <Checkbox
                    checked={isSelected}
                    size="small"
                    disableRipple
                    sx={{
                        padding: "2px",
                        mr: "4px",
                        color: "#C4C4C4",
                        "& .MuiSvgIcon-root": { fontSize: 16 },
                        "&.Mui-checked": { color: "#61D5A9" },
                    }}
                />
                <span style={{ fontSize: "12px", color: "#373F41" }}>{item.label}</span>
            </Box>
        );
    };

    return (
        <>
            {/* Trigger — shows a compact summary of selected values */}
            <Box
                onClick={handleOpen}
                sx={{
                    height: "28px",
                    fontSize: "12px",
                    fontFamily: "Inter, sans-serif",
                    backgroundColor: "white",
                    border: error ? "1px solid #d32f2f" : "1px solid #C4C4C4",
                    borderRadius: "4px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: "4px",
                    px: "10px",
                    cursor: "pointer",
                    "&:hover": { borderColor: error ? "#d32f2f" : "#61D5A9" },
                }}
            >
                <span
                    style={{
                        fontSize: "12px",
                        color: summaryText ? "#373F41" : (error ? "#d32f2f" : "#bab7b7"),
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                    }}
                >
                    {summaryText || "Select value(s)"}
                </span>
                <ArrowDropDown sx={{ fontSize: "20px", color: "#BAB7B7", mr: "-4px", flexShrink: 0 }} />
            </Box>

            {/* Popover-style dropdown */}
            {open && (
                <Box
                    sx={{
                        position: "absolute",
                        zIndex: 1300,
                        mt: "2px",
                        bgcolor: "white",
                        border: "1px solid #C4C4C4",
                        borderRadius: "8px",
                        boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
                        width: "100%",
                        minWidth: "200px",
                    }}
                >
                    {/* Search input */}
                    <Box sx={{ p: "8px 12px", borderBottom: "1px solid #eee" }}>
                        <TextField
                            size="small"
                            variant="standard"
                            placeholder="Search…"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            autoFocus
                            fullWidth
                            sx={{ fontSize: "12px", "& input": { fontSize: "12px", fontFamily: "Inter" } }}
                        />
                    </Box>

                    {/* Virtualized list */}
                    {filteredItems.length > 0 ? (
                        <FixedSizeList
                            height={Math.min(filteredItems.length, VIRTUAL_MAX_VISIBLE) * VIRTUAL_ROW_HEIGHT}
                            itemCount={filteredItems.length}
                            itemSize={VIRTUAL_ROW_HEIGHT}
                            width="100%"
                        >
                            {Row}
                        </FixedSizeList>
                    ) : (
                        <Box sx={{ p: "12px", textAlign: "center" }}>
                            <span style={{ fontSize: "12px", color: "#bab7b7" }}>No results</span>
                        </Box>
                    )}

                    {/* Footer — Select all / Clear all */}
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            px: "8px",
                            py: "4px",
                            borderTop: "1px solid #eee",
                        }}
                    >
                        <Button
                            size="small"
                            disableRipple
                            onClick={selectAllFiltered}
                            disabled={filteredItems.length === 0}
                            sx={{
                                fontSize: "11px",
                                fontFamily: "Inter",
                                fontWeight: 500,
                                textTransform: "none",
                                color: "#12794F",
                                minWidth: "unset",
                                px: "6px",
                                "&:hover": { backgroundColor: "rgba(18,121,79,0.08)" },
                            }}
                        >
                            Select all
                        </Button>
                        <Button
                            size="small"
                            disableRipple
                            onClick={clearAll}
                            disabled={selectedSet.size === 0}
                            sx={{
                                fontSize: "11px",
                                fontFamily: "Inter",
                                fontWeight: 500,
                                textTransform: "none",
                                color: "#BAB7B7",
                                minWidth: "unset",
                                px: "6px",
                                "&:hover": { color: "#373F41", backgroundColor: "transparent" },
                            }}
                        >
                            Clear all
                        </Button>
                    </Box>
                </Box>
            )}

            {/* Click-away listener */}
            {open && (
                <Box
                    onClick={handleClose}
                    sx={{ position: "fixed", inset: 0, zIndex: 1299 }}
                />
            )}
        </>
    );
};
